/**
 * QuestSectionDisplay
 * 
 * Displays quest section narrative and 2-3 choice buttons
 * Handles quest progression when player makes choices
 */

import React, { useState, useEffect } from 'react';
import { QuestSection, QuestPlaythrough, Campaign } from '../utils/questManager';
import { QuestProgressTracker } from './QuestProgressTracker';
import { AxialCoord } from '../types/terrain';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import './QuestSectionDisplay.css';

export interface QuestSectionDisplayProps {
  questPlaythrough: QuestPlaythrough;
  currentSection: QuestSection;
  onMakeChoice: (choiceId: string) => Promise<void>;
  onAbandonCampaign?: () => void;
  onInitiateCombat?: (monsterName: string, monsterStats: { hp: number; attackDie: number; damageDie: number }) => void;
  onFleeCombat?: () => void;
  isLoading?: boolean;
  outOfStaminaMessage?: string | null;
}

export const QuestSectionDisplay: React.FC<QuestSectionDisplayProps> = ({
  questPlaythrough,
  currentSection,
  onMakeChoice,
  onAbandonCampaign,
  onInitiateCombat,
  onFleeCombat,
  isLoading = false,
  outOfStaminaMessage = null,
}) => {
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  const [combatDecision, setCombatDecision] = useState<'fight' | 'flee' | null>(null);
  const [sponsorshipDecision, setSponsorshipDecision] = useState<'pending' | 'sponsored' | 'not-sponsored'>('pending');
  const [isSponsorshipProcessing, setIsSponsorshipProcessing] = useState(false);
  const audioPlayer = useAudioPlayer();
  const loadingRef = React.useRef<HTMLDivElement>(null);
  const renderCountRef = React.useRef(0);
  
  // Track component mount/unmount
  React.useEffect(() => {
    console.log('🎭 [QUEST_DISPLAY] Component MOUNTED');
    console.log('   Quest ID:', questPlaythrough.questId);
    console.log('   Section number:', currentSection.sectionNumber);
    console.log('   Has audioUrl:', !!currentSection.audioUrl);
    console.log('   AudioUrl:', currentSection.audioUrl);
    
    return () => {
      console.log('🎭 [QUEST_DISPLAY] Component UNMOUNTING');
      console.log('   Quest ID:', questPlaythrough.questId);
      console.log('   Section number:', currentSection.sectionNumber);
    };
  }, []);
  
  // Track renders
  renderCountRef.current++;
  console.log(`🎨 [QUEST_DISPLAY] Render #${renderCountRef.current}`);
  console.log('   Quest ID:', questPlaythrough.questId);
  console.log('   Section:', currentSection.sectionNumber);
  console.log('   AudioUrl:', currentSection.audioUrl);
  
  // Reset selectedChoiceId, combatDecision and load audio when section changes
  useEffect(() => {
    console.log('═══════════════════════════════════════════════════════');
    console.log('🎵 [QUEST_DISPLAY] Audio useEffect TRIGGERED');
    console.log('   Timestamp:', new Date().toISOString());
    console.log('   Dependencies changed:');
    console.log('     - questId:', questPlaythrough.questId);
    console.log('     - sectionNumber:', currentSection.sectionNumber);
    console.log('     - audioUrl:', currentSection.audioUrl || 'MISSING');
    console.log('     - hasAudioUrl:', !!currentSection.audioUrl);
    console.log('     - convergencePhase:', currentSection.convergencePhase);
    console.log('     - isBottleneck:', currentSection.isBottleneck);
    console.log('═══════════════════════════════════════════════════════');
    
    setSelectedChoiceId(null); // Reset choice selection for new section
    setCombatDecision(null); // Reset combat decision for new section
    
    if (currentSection.audioUrl) {
      console.log('📞 [QUEST_DISPLAY] Calling audioPlayer.setAudioUrl with:', currentSection.audioUrl);
      console.log('   This is a QUEST audio URL');
      audioPlayer.setAudioUrl(currentSection.audioUrl);
      console.log('✅ [QUEST_DISPLAY] audioPlayer.setAudioUrl call completed');
    } else {
      console.warn('⚠️ [QUEST_DISPLAY] Quest section missing audio - Section may have been generated before audio implementation or audio generation failed server-side. Check Firebase Functions logs for:', {
        questId: questPlaythrough.questId,
        sectionNumber: currentSection.sectionNumber,
        cacheKey: `${questPlaythrough.questId}-s${currentSection.sectionNumber}`,
        expectedPath: `quest-narration/v*/[cacheKey].mp3`
      });
    }
    // NOTE: audioPlayer is intentionally excluded from dependencies to prevent infinite loop
    // The audioPlayer object from context is stable and doesn't need to be a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection.sectionNumber, currentSection.audioUrl, currentSection.convergencePhase, currentSection.isBottleneck, questPlaythrough.questId]);
  
  // Scroll loading message into view when it appears
  useEffect(() => {
    if (isLoading && loadingRef.current) {
      // Scroll the loading element into view immediately
      loadingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isLoading]);
  
  const handleChoiceClick = async (choiceId: string) => {
    if (isLoading || selectedChoiceId) return;
    
    // Stop current section's narration when making a choice
    audioPlayer.pause();
    
    setSelectedChoiceId(choiceId);
    
    try {
      await onMakeChoice(choiceId);
    } catch (error) {
      console.error('Error making quest choice:', error);
      setSelectedChoiceId(null);
    }
  };
  
  const handleSponsorQuest = async () => {
    if (!questPlaythrough || isSponsorshipProcessing) return;
    
    setIsSponsorshipProcessing(true);
    
    try {
      // Import sponsorship function
      const { sponsorQuest } = await import('../utils/questManager');
      const { getAuth } = await import('firebase/auth');
      
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        console.error('No user logged in');
        return;
      }
      
      const startHex = questPlaythrough.startHex || questPlaythrough.visitedHexes[0];
      
      if (!startHex) {
        console.error('No start hex found for sponsorship');
        return;
      }
      
      await sponsorQuest(questPlaythrough.questId, user.uid, startHex);
      
      console.log('✅ Quest sponsored successfully');
      setSponsorshipDecision('sponsored');
      
    } catch (error) {
      console.error('Error sponsoring quest:', error);
      alert('Failed to sponsor quest. Please try again.');
    } finally {
      setIsSponsorshipProcessing(false);
    }
  };

  const handleDontSponsor = async () => {
    if (!questPlaythrough || isSponsorshipProcessing) return;
    
    setIsSponsorshipProcessing(true);
    
    try {
      const { markQuestUnsponsored } = await import('../utils/questManager');
      
      await markQuestUnsponsored(questPlaythrough.questId);
      
      console.log('✅ Quest marked as not sponsored');
      setSponsorshipDecision('not-sponsored');
      
    } catch (error) {
      console.error('Error marking quest as unsponsored:', error);
      alert('Failed to process choice. Please try again.');
    } finally {
      setIsSponsorshipProcessing(false);
    }
  };
  
  // NEW: Combat handlers
  const handleFightClick = () => {
    if (!currentSection.hasCombat || !currentSection.monsterName || !currentSection.monsterStats) {
      console.error('Combat section missing required data');
      return;
    }
    
    setCombatDecision('fight');
    
    if (onInitiateCombat) {
      onInitiateCombat(currentSection.monsterName, currentSection.monsterStats);
    }
  };
  
  const handleFleeClick = () => {
    setCombatDecision('flee');
    
    if (onFleeCombat) {
      onFleeCombat();
    }
  };
  
  // Filter choices based on state requirements
  const availableChoices = currentSection.choices.filter(choice => {
    if (!choice.requiresState) return true;
    
    // Check if all required state conditions are met
    for (const [key, value] of Object.entries(choice.requiresState)) {
      if (questPlaythrough.questState[key] !== value) {
        return false;
      }
    }
    return true;
  });
  
  // Debug logging for sections with no available choices
  if (!currentSection.concludes && availableChoices.length === 0) {
    console.error('⚠️ Section has no available choices!', {
      sectionNumber: currentSection.sectionNumber,
      totalChoices: currentSection.choices.length,
      choices: currentSection.choices,
      currentState: questPlaythrough.questState,
      concludes: currentSection.concludes
    });
  }
  
  return (
    <div className="quest-section-display">
      {/* Progress Tracker */}
      <QuestProgressTracker
        questPlaythrough={questPlaythrough}
      />
      
      {/* Loading State - Show instead of content when generating */}
      {isLoading ? (
        <div 
          ref={loadingRef}
          className="quest-section-loading" 
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            minHeight: '200px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div className="quest-loading-spinner" />
          <p style={{ marginTop: '20px', fontSize: '18px', fontFamily: "'Cinzel', serif" }}>
            QUESTING...
          </p>
        </div>
      ) : (
        <>
          {/* Narrative */}
          <div className="quest-section-narrative">
            <p className="story-line">{currentSection.narrative}</p>
          </div>
          
          {/* Combat Decision UI */}
          {currentSection.hasCombat && currentSection.monsterName && !combatDecision && (
            <div className="quest-combat-decision" style={{
              marginTop: '24px',
              padding: '20px',
              background: 'rgba(139, 69, 19, 0.1)',
              border: '2px solid #8b4513',
              borderRadius: '8px',
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '20px',
              }}>
                <p style={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#8b4513',
                  margin: '0 0 8px 0',
                }}>
                  ⚔️ {currentSection.monsterName}
                </p>
              </div>
              
              <p style={{
                fontFamily: "'Cinzel', serif",
                fontSize: '14px',
                fontWeight: 600,
                color: '#8b5a2b',
                textAlign: 'center',
                margin: '0 0 16px 0',
              }}>
                What do you do?
              </p>
              
              <button
                className="quest-combat-fight-button"
                onClick={handleFightClick}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  marginBottom: '12px',
                  background: '#d4a574',
                  border: '3px solid #8b4513',
                  borderRadius: '4px',
                  fontFamily: "'Cinzel', serif",
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#4a4035',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#c89860';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#d4a574';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                ⚔️ Stand and Fight
              </button>
              
              <button
                className="quest-combat-flee-button"
                onClick={handleFleeClick}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: 'rgba(139, 69, 19, 0.2)',
                  border: '2px solid rgba(139, 69, 19, 0.4)',
                  borderRadius: '4px',
                  fontFamily: "'Lora', serif",
                  fontSize: '14px',
                  color: '#8b4513',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(139, 69, 19, 0.3)';
                  e.currentTarget.style.borderColor = '#8b4513';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(139, 69, 19, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(139, 69, 19, 0.4)';
                }}
              >
                Attempt to Flee (Quest will FAIL)
              </button>
            </div>
          )}
          
          {/* Out of Stamina Warning */}
          {outOfStaminaMessage && !currentSection.concludes && (
            <div className="quest-stamina-warning" style={{
              padding: '16px',
              margin: '20px 0',
              background: 'rgba(255, 193, 7, 0.15)',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              fontFamily: "'Lora', serif"
            }}>
              <p style={{ 
                color: '#d97706', 
                fontWeight: 'bold', 
                marginBottom: '8px',
                fontSize: '16px',
                textAlign: 'center'
              }}>
                ⚠️ Out of Stamina
              </p>
              <p style={{ 
                margin: '0',
                fontSize: '14px',
                lineHeight: '1.6',
                textAlign: 'center',
                color: '#92400e'
              }}>
                {outOfStaminaMessage}
              </p>
            </div>
          )}
          
          {/* Regular Choices (only shown if NOT combat section) */}
          {!currentSection.hasCombat && !currentSection.concludes && availableChoices.length > 0 && (
            <div className="quest-section-choices">
              <p className="quest-section-prompt">What do you do?</p>
              {availableChoices.map((choice) => (
                <button
                  key={choice.id}
                  className={`quest-choice-button ${selectedChoiceId === choice.id ? 'selected' : ''}`}
                  onClick={() => handleChoiceClick(choice.id)}
                  disabled={isLoading || selectedChoiceId !== null || !!outOfStaminaMessage}
                >
                  <span className="quest-choice-text">{choice.text}</span>
                </button>
              ))}
              
              {/* Abandon Quest Button - Always visible */}
              {onAbandonCampaign && (
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button
                    onClick={onAbandonCampaign}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(139, 69, 19, 0.85)',
                      color: '#f5e6d3',
                      border: '2px solid #8b4513',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontFamily: "'Cinzel', serif",
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(139, 69, 19, 0.95)';
                      e.currentTarget.style.borderColor = '#6b3410';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(139, 69, 19, 0.85)';
                      e.currentTarget.style.borderColor = '#8b4513';
                    }}
                    title="Give up on this quest"
                  >
                    Abandon Quest
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* No Choices Available Error - only show for non-combat sections */}
          {!currentSection.concludes && !currentSection.hasCombat && availableChoices.length === 0 && (
            <div className="quest-section-error" style={{
              padding: '20px',
              margin: '20px 0',
              background: 'rgba(200, 50, 50, 0.2)',
              border: '2px solid #c83232',
              borderRadius: '8px',
              fontFamily: "'Lora', serif"
            }}>
              <p style={{ color: '#ff6b6b', fontWeight: 'bold', marginBottom: '10px' }}>
                ⚠️ No Available Choices
              </p>
              <p style={{ marginBottom: '10px' }}>
                This section has {currentSection.choices.length} choice(s), but none are available based on your current quest state.
              </p>
              <p style={{ fontSize: '14px', opacity: 0.8, marginBottom: '15px' }}>
                This quest is stuck and must be abandoned.
              </p>
              {onAbandonCampaign && (
                <button
                  onClick={onAbandonCampaign}
                  style={{
                    padding: '10px 20px',
                    background: '#c83232',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    fontFamily: "'Cinzel', serif",
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Abandon Quest
                </button>
              )}
            </div>
          )}
          
          {/* Quest Conclusion */}
          {currentSection.concludes && currentSection.outcome && (
            <div className="quest-section-conclusion">
              <p className="quest-conclusion-text">{currentSection.outcome}</p>
              <p className="quest-completion-notice">Quest Complete!</p>
              
              {/* Sponsorship Choice UI */}
              {sponsorshipDecision === 'pending' && (
                <div className="quest-sponsorship-choice" style={{
                  marginTop: '24px',
                  padding: '20px',
                  background: 'rgba(200, 150, 50, 0.15)',
                  border: '2px solid #c8963280',
                  borderRadius: '8px'
                }}>
                  <p style={{ 
                    textAlign: 'center', 
                    marginBottom: '16px',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    Sponsor this quest?
                  </p>
                  <p style={{ 
                    textAlign: 'center', 
                    marginBottom: '20px',
                    fontSize: '14px',
                    opacity: 0.9
                  }}>
                    Sponsored quests are bound to this hex and can be replayed by other players.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button
                      onClick={handleSponsorQuest}
                      disabled={isSponsorshipProcessing}
                      style={{
                        padding: '12px 32px',
                        background: '#4a7c59',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: isSponsorshipProcessing ? 'not-allowed' : 'pointer',
                        opacity: isSponsorshipProcessing ? 0.6 : 1
                      }}
                    >
                      {isSponsorshipProcessing ? 'Processing...' : 'Sponsor this quest'}
                    </button>
                    <button
                      onClick={handleDontSponsor}
                      disabled={isSponsorshipProcessing}
                      style={{
                        padding: '12px 32px',
                        background: 'rgba(150, 150, 150, 0.3)',
                        color: 'white',
                        border: '1px solid rgba(200, 200, 200, 0.5)',
                        borderRadius: '6px',
                        fontSize: '16px',
                        cursor: isSponsorshipProcessing ? 'not-allowed' : 'pointer',
                        opacity: isSponsorshipProcessing ? 0.6 : 1
                      }}
                    >
                      Don't sponsor
                    </button>
                  </div>
                </div>
              )}
              
              {/* Sponsored Confirmation */}
              {sponsorshipDecision === 'sponsored' && (
                <div className="quest-sponsored-confirmation" style={{
                  marginTop: '24px',
                  padding: '20px',
                  background: 'rgba(74, 124, 89, 0.2)',
                  border: '2px solid #4a7c59',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold', 
                    color: '#4a7c59',
                    marginBottom: '12px'
                  }}>
                    ✓ Quest Sponsored!
                  </p>
                  <p style={{ fontSize: '14px', marginBottom: '16px' }}>
                    This quest is now bound to this hex. Other players can replay it here.
                  </p>
                  <button
                    onClick={() => {
                      // Close quest UI - trigger parent to clear quest state
                      if (onAbandonCampaign) {
                        onAbandonCampaign();
                      }
                    }}
                    style={{
                      padding: '10px 24px',
                      background: '#4a7c59',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
              
              {/* Not Sponsored Confirmation */}
              {sponsorshipDecision === 'not-sponsored' && (
                <div className="quest-not-sponsored-confirmation" style={{
                  marginTop: '24px',
                  padding: '20px',
                  background: 'rgba(150, 150, 150, 0.2)',
                  border: '2px solid rgba(200, 200, 200, 0.5)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{ fontSize: '14px', marginBottom: '16px' }}>
                    Quest not sponsored.
                  </p>
                  <button
                    onClick={() => {
                      if (onAbandonCampaign) {
                        onAbandonCampaign();
                      }
                    }}
                    style={{
                      padding: '10px 24px',
                      background: 'rgba(150, 150, 150, 0.5)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

